/* Voice search for the header search field.
 *
 * Security / privacy posture:
 *  - Uses the browser's built-in Web Speech API. No API keys, no third-party
 *    SDK, nothing for an attacker to lift out of the theme source.
 *  - The mic only ever starts from a real user click (a gesture is required by
 *    the browser anyway) and stops on the first result, on silence, on Escape,
 *    or when the search modal closes. It is never left open.
 *  - Nothing is recorded, stored, or sent anywhere by this theme. The
 *    transcript is written straight into the existing search input and the
 *    theme's own predictive search handles it from there.
 *  - Requires HTTPS. On http:// the browser blocks the mic and we hide the
 *    button rather than showing one that cannot work.
 *  - NOTE: in Chrome/Edge the Web Speech API sends audio to Google's servers
 *    for transcription. Safari uses on-device recognition. This is the
 *    browser's behaviour, not the theme's — but it belongs in your privacy
 *    policy. See the note in the theme settings.
 */
class VoiceSearch extends HTMLElement {
  connectedCallback() {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    /* No support (Firefox), or insecure context -> remove the control entirely
       rather than offer a button that fails. */
    if (!SpeechRecognition || !window.isSecureContext) {
      this.remove();
      return;
    }

    this.button = this.querySelector('[data-voice-button]');
    this.status = this.querySelector('[data-voice-status]');
    this.input = this.closest('form')?.querySelector('input[name="q"]');
    if (!this.button || !this.input) return;

    this.autosubmit = this.dataset.autosubmit === 'true';
    this.listening = false;

    this.recognition = new SpeechRecognition();
    this.recognition.lang = this.dataset.lang || 'en-GB';
    this.recognition.interimResults = true;
    this.recognition.continuous = false;
    this.recognition.maxAlternatives = 1;

    this.recognition.onresult = (event) => {
      let transcript = '';
      let isFinal = false;
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
        if (event.results[i].isFinal) isFinal = true;
      }
      transcript = transcript.trim();
      if (!transcript) return;

      this.input.value = transcript;
      /* Let the theme's predictive search react exactly as if it were typed. */
      this.input.dispatchEvent(new Event('input', { bubbles: true }));

      if (isFinal) {
        this.setStatus(transcript);
        this.stop();
        if (this.autosubmit) this.input.form?.submit();
      }
    };

    this.recognition.onerror = (event) => {
      const messages = {
        'not-allowed': this.dataset.msgDenied || 'Microphone access was blocked.',
        'service-not-allowed':
          this.dataset.msgDenied || 'Microphone access was blocked.',
        'no-speech': this.dataset.msgNoSpeech || "Didn't catch that. Try again.",
        network: this.dataset.msgNetwork || 'Voice search is unavailable.',
      };
      this.setStatus(messages[event.error] || this.dataset.msgError || 'Try again.');
      this.stop();
    };

    this.recognition.onend = () => this.stop();

    this.button.addEventListener('click', () => this.toggle());

    /* Never leave the mic live behind a closed modal. */
    this.onKey = (e) => {
      if (e.key === 'Escape' && this.listening) this.stop();
    };
    document.addEventListener('keydown', this.onKey);

    this.observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting && this.listening) this.stop();
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('keydown', this.onKey);
    if (this.observer) this.observer.disconnect();
  }

  toggle() {
    if (this.listening) {
      this.stop();
    } else {
      this.start();
    }
  }

  start() {
    try {
      this.recognition.start();
    } catch (e) {
      return; /* already running */
    }
    this.listening = true;
    this.classList.add('is-listening');
    this.button.setAttribute('aria-pressed', 'true');
    this.setStatus(this.dataset.msgListening || 'Listening…');
  }

  stop() {
    if (this.listening) {
      try {
        this.recognition.stop();
      } catch (e) {
        /* no-op */
      }
    }
    this.listening = false;
    this.classList.remove('is-listening');
    this.button.setAttribute('aria-pressed', 'false');
  }

  setStatus(text) {
    if (this.status) this.status.textContent = text;
  }
}

if (!customElements.get('voice-search')) {
  customElements.define('voice-search', VoiceSearch);
}
